import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { TProductMetaData } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { TCustomerType } from "redux/reducers/models/facility";
import { formatDate } from "helpers/Helpers";

import Spin from "components/spin/spin";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/";
import { MEMBERSHIP_EXPIRY_OPTIONS } from "../Membership";
import Input from "components/form/input";
import moment from "moment";
interface IProductMembershipOptionsProps {
  isVisible: boolean;
  applicationFilter: "green_fee" | "power_cart" | null;
  optionProps: TProductMetaData;
  setOptionProps: React.Dispatch<React.SetStateAction<TProductMetaData>>;
}

export default function ProductMembershipOptions(props: IProductMembershipOptionsProps) {
  const { isVisible, applicationFilter, optionProps, setOptionProps } = props;
  const { t } = useTranslation();
  const { Option } = Select;

  const [dateState] = useState({
    hasExpiry: false,
    expiryDate: new Date(),
  });

  const [types, setTypes] = useState<TCustomerType[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (types !== undefined) {
      return;
    }
    void loadCustomerTypes(source.token);
    return () => source.cancel();
  }, [isVisible]);

  useEffect(() => {
    // reset entire options if removing applicationFilter -- always reset customer type on applicationFilter change
    if (applicationFilter === null) {
      setOptionProps({ customer_type_id: -1, expiry: null, membership_type: "" });
    } else {
      setOptionProps(prev => ({ ...prev, customer_type_id: -1 }));
    }
  }, [applicationFilter]);

  async function loadCustomerTypes(token?: CancelToken) {
    const res = await GetCustomerType(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    setTypes(res.status !== StatusCode.OK ? [] : res.data);
  }

  function getExpiryDate() {
    switch (optionProps?.expiry_type) {
      case "end_of_year":
        return moment()?.endOf("year")?.format('ll');
      case "one_year":
        return moment()?.add(1, "year")?.format("ll");
      default:
        return moment(optionProps?.expiry, "YYYY-MM-DD")?.format("ll");
    }
  }

  return (
    <>
      {isVisible && (
        <Card title={t("secure.facility.product.product_new.044")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t("secure.facility.product.product_new.045")}
                  onChange={(value: string) =>
                    setOptionProps({
                      ...optionProps,
                      membership_type: value,
                    })
                  }
                  className="dropdown-max_height"
                >
                  {membershipOptions(t).map((type: any, index: number) => {
                    return (
                      <Option key={index} value={type.id} name={type.title}>
                        {type.title}
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  label={t("secure.facility.product.product_new.048")}
                  onChange={(value: any) =>
                    setOptionProps({
                      ...optionProps,
                      customer_type_id: value,
                    })
                  }
                  defaultValue={optionProps.customer_type_id}
                  className="dropdown-max_height"
                >
                  {types ? (
                    types
                      .filter(val => val.application === applicationFilter)
                      .map(type => (
                        <Option key={type.id} value={type.id} name={type.title}>
                          {type.title}
                        </Option>
                      ))
                  ) : (
                    <Spin />
                  )}
                </Select>
              </FormLayout.Group>

              <FormLayout.Group>
              <Select
                label={t("Membership Expiry")}
                placeholder={t("Select membership expiry")}
                onChange={value => setOptionProps({ ...optionProps, expiry_type: value })}
                defaultValue={optionProps?.expiry_type ?? "no_expiry"}
              >
                {MEMBERSHIP_EXPIRY_OPTIONS(t)?.map((option, i: number) => {
                    return (
                      <Option key={i} value={option?.value}>
                        {option?.label}
                      </Option>
                    );
                  })}
              </Select>
                <div className="membership-expiry">
                  {optionProps?.expiry_type === "custom_date" ? (
                    <div id="membership-date-selector">
                      <DatePickerInput
                        months={1}
                        label={("Expires On")}
                        position={"center"}
                        startingDate={dateState.expiryDate}
                        setStartingDate={(selectedDate: Date) =>
                          setOptionProps({
                            ...optionProps,
                            expiry: formatDate(selectedDate),
                          })
                        }
                      />
                    </div>
                  ) : optionProps?.expiry_type !== "no_expiry" ? (
                    <Input 
                    label={t("Expires on")} 
                    readOnly
                     value={getExpiryDate()}/>
                  ) : null}
                </div>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      )}
    </>
  );
}

const membershipOptions = (t: TFunction<"translation", undefined>) => {
  return [
    {
      id: "single_course",
      title: t("secure.facility.product.product_new.002"),
    },
    {
      id: "multi_course",
      title: t("secure.facility.product.product_new.003"),
    },
  ];
};
